/**
 * ClassificationCriteriaPanel Component
 * 
 * Allows users to adjust deal classification thresholds:
 * - Minimum profit target
 * - Maximum investment % of ARV
 * - Rehab tolerance ratio
 * 
 * These settings ACTUALLY affect the Deal Classification (GOOD/MARGINAL/NO PASS)
 * by calling the reclassify endpoint when "Re-analyze with My Criteria" is clicked.
 * 
 * Bilingual support: EN/ES
 */

import React, { useState, useEffect } from 'react';
import { Settings2, Save, RotateCcw, HelpCircle, Sliders, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';
import { analysisAPI } from '../api';

// Translation keys
const translations = {
  en: {
    title: 'Adjust Classification Criteria',
    subtitle: 'Customize thresholds to match your investment strategy',
    minProfit: 'Minimum Profit Target',
    minProfitHelp: 'Deals below this profit will be flagged as risky',
    maxInvestmentArv: 'Max Investment % of Est. Value',
    maxInvestmentHelp: 'Total cost (purchase + rehab) should not exceed this % of estimated value',
    rehabTolerance: 'Rehab Tolerance Ratio',
    rehabToleranceHelp: 'Maximum acceptable rehab cost as a ratio of purchase price',
    savePreferences: 'Save Preferences',
    resetDefaults: 'Reset to Defaults',
    reanalyze: 'Re-analyze with My Criteria',
    reanalyzing: 'Re-analyzing...',
    reclassified: 'Classification Updated!',
    conservative: 'Conservative',
    moderate: 'Moderate',
    aggressive: 'Aggressive',
    settingsSaved: 'Classification preferences saved',
    settingsReset: 'Settings reset to conservative defaults',
    criteriaChanged: 'Criteria changed - click "Re-analyze" to update classification'
  },
  es: {
    title: 'Ajustar Criterios de Clasificación',
    subtitle: 'Personaliza los umbrales según tu estrategia de inversión',
    minProfit: 'Meta de Ganancia Mínima',
    minProfitHelp: 'Tratos por debajo de esta ganancia serán marcados como riesgosos',
    maxInvestmentArv: 'Inversión Máx. % de Valor Est.',
    maxInvestmentHelp: 'El costo total (compra + rehab) no debe exceder este % del valor estimado',
    rehabTolerance: 'Tolerancia de Rehab',
    rehabToleranceHelp: 'Costo máximo aceptable de rehab como ratio del precio de compra',
    savePreferences: 'Guardar Preferencias',
    resetDefaults: 'Restablecer Predeterminados',
    reanalyze: 'Re-analizar con Mis Criterios',
    reanalyzing: 'Re-analizando...',
    reclassified: '¡Clasificación Actualizada!',
    conservative: 'Conservador',
    moderate: 'Moderado',
    aggressive: 'Agresivo',
    settingsSaved: 'Preferencias de clasificación guardadas',
    settingsReset: 'Configuración restablecida a valores conservadores',
    criteriaChanged: 'Criterios cambiados - haz clic en "Re-analizar" para actualizar'
  }
};

// Default moderate values
const DEFAULT_CRITERIA = {
  min_profit: 20000,
  max_investment_pct_arv: 75,
  max_rehab_ratio: 1.0
};

// Preset profiles
const PRESETS = {
  conservative: {
    min_profit: 30000,
    max_investment_pct_arv: 70,
    max_rehab_ratio: 0.75
  },
  moderate: {
    min_profit: 20000,
    max_investment_pct_arv: 75,
    max_rehab_ratio: 1.0
  },
  aggressive: {
    min_profit: 15000,
    max_investment_pct_arv: 85,
    max_rehab_ratio: 1.75
  }
};

const ClassificationCriteriaPanel = ({ 
  criteria, 
  onCriteriaChange, 
  onSave,
  onReclassify,  // NEW: Callback to update parent's classification
  analysisData,  // NEW: Required for reclassification (purchase_price, rehab_cost, arv, net_profit)
  lang = 'en',
  showToast
}) => {
  const t = translations[lang] || translations.en;
  const [localCriteria, setLocalCriteria] = useState(criteria || DEFAULT_CRITERIA);
  const [expanded, setExpanded] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isReclassifying, setIsReclassifying] = useState(false);
  const [justReclassified, setJustReclassified] = useState(false);
  
  useEffect(() => {
    if (criteria) {
      setLocalCriteria(criteria);
    }
  }, [criteria]);
  
  const handleChange = (field, value) => {
    const newCriteria = { ...localCriteria, [field]: value };
    setLocalCriteria(newCriteria);
    setHasChanges(true);
    setJustReclassified(false);
    if (onCriteriaChange) {
      onCriteriaChange(newCriteria);
    }
  };
  
  const handlePresetClick = (presetKey) => {
    const preset = PRESETS[presetKey];
    setLocalCriteria(preset);
    setHasChanges(true);
    setJustReclassified(false);
    if (onCriteriaChange) {
      onCriteriaChange(preset);
    }
  };
  
  const handleReclassify = async () => {
    if (!analysisData || isReclassifying) return;
    
    setIsReclassifying(true);
    try {
      const response = await analysisAPI.reclassify({
        purchase_price: analysisData.purchase_price,
        rehab_cost: analysisData.rehab_cost,
        arv: analysisData.arv,
        net_profit: analysisData.net_profit,
        strategy: analysisData.strategy || 'flip',
        user_criteria: localCriteria,
        // Without this the endpoint defaults to English and the re-classified summary
        // and improvement actions come back in English under Spanish headings.
        language: lang
      });
      
      if (response.data?.success && response.data?.classification) {
        // Update parent component with new classification
        if (onReclassify) {
          onReclassify(response.data.classification);
        }
        setJustReclassified(true);
        setHasChanges(false);
        if (showToast) {
          showToast(t.reclassified);
        }
        // Reset the success state after 3 seconds
        setTimeout(() => setJustReclassified(false), 3000);
      }
    } catch (error) {
      console.error('Reclassification failed:', error);
      if (showToast) {
        showToast(lang === 'en' ? 'Reclassification failed' : 'Error al re-clasificar');
      }
    } finally {
      setIsReclassifying(false);
    }
  };
  
  const handleSave = () => {
    localStorage.setItem('chekdeal_classification_criteria', JSON.stringify(localCriteria));
    if (onSave) {
      onSave(localCriteria);
    }
    if (showToast) {
      showToast(t.settingsSaved);
    }
  };
  
  const handleReset = () => {
    const moderate = PRESETS.moderate;
    setLocalCriteria(moderate);
    setHasChanges(true);
    setJustReclassified(false);
    if (onCriteriaChange) {
      onCriteriaChange(moderate);
    }
    if (showToast) {
      showToast(t.settingsReset);
    }
  };
  
  // Determine which preset is currently active
  const activePreset = Object.entries(PRESETS).find(([key, preset]) => 
    preset.min_profit === localCriteria.min_profit &&
    preset.max_investment_pct_arv === localCriteria.max_investment_pct_arv &&
    preset.max_rehab_ratio === localCriteria.max_rehab_ratio
  )?.[0] || null;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-4" data-testid="classification-criteria-panel">
      {/* Header - Collapsible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
        data-testid="toggle-criteria-panel"
      >
        <div className="flex items-center gap-2">
          <Sliders className="w-5 h-5 text-indigo-600" />
          <div className="text-left">
            <h4 className="text-sm font-semibold text-slate-800">{t.title}</h4>
            <p className="text-xs text-slate-500">{t.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && !justReclassified && (
            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full animate-pulse">
              {lang === 'en' ? 'Changed' : 'Cambiado'}
            </span>
          )}
          {justReclassified && (
            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {lang === 'en' ? 'Updated' : 'Actualizado'}
            </span>
          )}
          <Settings2 className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {/* Preset Buttons */}
          <div className="flex gap-2 mt-4 mb-4">
            {Object.entries(PRESETS).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => handlePresetClick(key)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  activePreset === key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`preset-${key}`}
              >
                {t[key]}
              </button>
            ))}
          </div>
          
          {/* Criteria Inputs */}
          <div className="space-y-4">
            {/* Minimum Profit */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                  {t.minProfit}
                  <span className="group relative">
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                    <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block w-48 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-10">
                      {t.minProfitHelp}
                    </span>
                  </span>
                </label>
                <span className="text-sm font-semibold text-indigo-600">
                  ${localCriteria.min_profit?.toLocaleString()}
                </span>
              </div>
              <input
                type="range"
                min={10000}
                max={50000}
                step={5000}
                value={localCriteria.min_profit}
                onChange={(e) => handleChange('min_profit', parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                data-testid="min-profit-slider"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>$10k</span>
                <span>$50k</span>
              </div>
            </div>
            
            {/* Max Investment % ARV */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                  {t.maxInvestmentArv}
                  <span className="group relative">
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                    <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block w-48 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-10">
                      {t.maxInvestmentHelp}
                    </span>
                  </span>
                </label>
                <span className="text-sm font-semibold text-indigo-600">
                  {localCriteria.max_investment_pct_arv}%
                </span>
              </div>
              <input
                type="range"
                min={65}
                max={85}
                step={5}
                value={localCriteria.max_investment_pct_arv}
                onChange={(e) => handleChange('max_investment_pct_arv', parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                data-testid="max-arv-slider"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>65%</span>
                <span>85%</span>
              </div>
            </div>
            
            {/* Rehab Tolerance */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                  {t.rehabTolerance}
                  <span className="group relative">
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                    <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block w-48 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-10">
                      {t.rehabToleranceHelp}
                    </span>
                  </span>
                </label>
                <span className="text-sm font-semibold text-indigo-600">
                  {(localCriteria.max_rehab_ratio * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min={50}
                max={200}
                step={25}
                value={localCriteria.max_rehab_ratio * 100}
                onChange={(e) => handleChange('max_rehab_ratio', parseInt(e.target.value) / 100)}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                data-testid="rehab-tolerance-slider"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>50%</span>
                <span>200%</span>
              </div>
            </div>
          </div>
          
          {/* Criteria Changed Warning */}
          {hasChanges && !justReclassified && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 text-center">
                {t.criteriaChanged}
              </p>
            </div>
          )}
          
          {/* RE-ANALYZE BUTTON - Primary CTA */}
          <button
            onClick={handleReclassify}
            disabled={isReclassifying || !analysisData}
            className={`w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-base transition-all ${
              isReclassifying 
                ? 'bg-indigo-400 text-white cursor-wait'
                : justReclassified
                  ? 'bg-emerald-600 text-white'
                  : hasChanges
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
            data-testid="reclassify-btn"
          >
            {isReclassifying ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t.reanalyzing}
              </>
            ) : justReclassified ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                {t.reclassified}
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                {t.reanalyze}
              </>
            )}
          </button>
          
          {/* Secondary Actions */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleReset}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
              data-testid="reset-criteria-btn"
            >
              <RotateCcw className="w-4 h-4" />
              {t.resetDefaults}
            </button>
            <button
              onClick={handleSave}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
              data-testid="save-criteria-btn"
            >
              <Save className="w-4 h-4" />
              {t.savePreferences}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassificationCriteriaPanel;
